import { useState } from 'react'
import { ReloadOutlined } from '@ant-design/icons'
import { Crosshair, Minus } from 'lucide-react'
import { Tooltip } from 'antd'
import { Button } from '@/components/ui/button'
import { SlamContextMenu } from './SlamContextMenu'

import type { SlamFloorplanCanvasProps } from '../types'
import { useSlamCanvasInteraction } from '../../hook/hook slam/useSlamCanvasInteraction'

type ControlButtonProps = {
  label: React.ReactNode
  ariaLabel?: string
  disabled?: boolean
  className: string
  onClick: () => void
}

type ControlRowProps = {
  label: string
  children: React.ReactNode
  className?: string
  labelClassName?: string
}

type ResetControlButtonProps = {
  ariaLabel: string
  className: string
  iconClassName: string
  onClick: () => void
}

type SlamControlPanelProps = {
  isControlPanelCollapsed: boolean
  onCollapseChange: (collapsed: boolean) => void
  requiresHomePlacement: boolean
  hasBackgroundImage: boolean
  canZoomIn: boolean
  canZoomOut: boolean
  canIncreaseOverlayScale: boolean
  canDecreaseOverlayScale: boolean
  actions: {
    onZoomIn: () => void
    onZoomOut: () => void
    onResetZoom: () => void
    onIncreaseOverlayScale: () => void
    onDecreaseOverlayScale: () => void
    onResetOverlayScale: () => void
    onRotateOverlayLeft: () => void
    onRotateOverlayRight: () => void
    onResetOverlayRotation: () => void
  }
}

type HoverInfoBadgeProps = {
  x: number
  y: number
  scale: number
  hasBackgroundImage: boolean
  overlayScale: number
  overlayRotationDeg: string
}

const panelToggleButtonClass =
  'border border-[#A85D3D] text-[#A85D3D] hover:!bg-[#CE7A58] hover:!text-[#fef3f0]'

const controlButtonClass =
  'rounded-md border border-[#CE7A58] bg-white leading-none font-semibold text-[#A85D3D] shadow-sm transition-all enabled:cursor-pointer enabled:hover:bg-[#fbeae4] enabled:hover:shadow enabled:hover:scale-[1.02] enabled:active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#CE7A58]/50 disabled:cursor-not-allowed disabled:border-[#f2d7cb] disabled:text-[#d9b8aa]'

const resetButtonClass =
  'ml-1 flex items-center justify-center rounded-md border border-[#CE7A58] p-0 text-[#A85D3D] transition-all hover:cursor-pointer hover:bg-[#fbeae4] hover:shadow-sm hover:scale-[1.02] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#CE7A58]/50'

function stopPointerDown(e: React.PointerEvent<HTMLElement>) {
  e.stopPropagation()
}

function stopClick(e: React.MouseEvent<HTMLElement>, onClick?: () => void) {
  e.stopPropagation()
  onClick?.()
}

function ControlButton({
  label,
  ariaLabel,
  disabled,
  className,
  onClick,
}: ControlButtonProps) {
  return (
    <Button
      htmlType="button"
      variant="ghost"
      aria-label={ariaLabel}
      disabled={disabled}
      className={className}
      onPointerDown={stopPointerDown}
      onClick={(e) => stopClick(e, onClick)}
    >
      {label}
    </Button>
  )
}

function ControlRow({
  label,
  children,
  className = '',
  labelClassName = 'text-[11px] font-semibold text-gray-600',
}: ControlRowProps) {
  return (
    <div
      className={`flex items-center justify-between gap-2 rounded-xl border border-gray-100 bg-gradient-to-r from-gray-50 to-white px-2.5 py-2 ${className}`}
    >
      <span className={labelClassName}>{label}</span>
      <div className="flex items-center gap-1">{children}</div>
    </div>
  )
}

function ResetControlButton({
  ariaLabel,
  className,
  iconClassName,
  onClick,
}: ResetControlButtonProps) {
  return (
    <ControlButton
      ariaLabel={ariaLabel}
      className={`${resetButtonClass} ${className}`}
      onClick={onClick}
      label={<ReloadOutlined className={iconClassName} />}
    />
  )
}

function SlamControlPanel({
  isControlPanelCollapsed,
  onCollapseChange,
  requiresHomePlacement,
  hasBackgroundImage,
  canZoomIn,
  canZoomOut,
  canIncreaseOverlayScale,
  canDecreaseOverlayScale,
  actions,
}: SlamControlPanelProps) {
  if (isControlPanelCollapsed) {
    return (
      <div className="absolute right-2 top-2 z-30 md:right-3 md:top-3">
        <Button
          htmlType="button"
          variant="outline"
          size="md"
          className={panelToggleButtonClass}
          onPointerDown={stopPointerDown}
          onClick={(e) => stopClick(e, () => onCollapseChange(false))}
        >
          Không gian thao tác
        </Button>
      </div>
    )
  }

  return (
    <div className="absolute right-2 top-2 z-30 w-[calc(100%-16px)] max-w-[320px] rounded-2xl border border-white/70 bg-white/88 p-3 shadow-[0_12px_36px_rgba(26,26,26,0.08)] backdrop-blur-md md:right-3 md:top-3 md:w-auto md:min-w-[280px]">
      <div className="mb-2 flex items-center justify-between px-1">
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#A85D3D]">
          Không gian thao tác
        </span>
        <div className="flex items-center gap-2">
          <span
            className={`rounded-full px-2 py-1 text-[10px] font-medium ${
              !hasBackgroundImage
                ? 'bg-amber-50 text-amber-700 border border-amber-200/60'
                : 'bg-[#fff3ed] text-[#A85D3D]'
            }`}
          >
            {requiresHomePlacement
              ? 'Đặt Home'
              : hasBackgroundImage
                ? 'Chỉnh SLAM'
                : 'SLAM Gốc (Không có sơ đồ trung tâm)'}
          </span>
          <Button
            htmlType="button"
            variant="outline"
            size="sm"
            aria-label="Thu gọn không gian thao tác"
            className={`h-7 w-7 min-w-7 p-0 ${panelToggleButtonClass}`}
            onPointerDown={stopPointerDown}
            onClick={(e) => stopClick(e, () => onCollapseChange(true))}
          >
            <Minus className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {requiresHomePlacement && (
        <div className="mb-2 rounded-lg border border-[#f4d4c8] bg-[#fff6f2] px-2 py-1.5 text-[11px] font-medium text-[#A85D3D]">
          Chấm hoặc kéo Home, sau đó bấm Lưu để hiển thị lớp SLAM.
        </div>
      )}

      {/* Hiển thị gợi ý khi không có sơ đồ mặt bằng */}
      {!hasBackgroundImage && !requiresHomePlacement && (
        <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50/80 px-2.5 py-1.5 text-[11px] text-amber-800">
          Robot chưa có ảnh sơ đồ trung tâm. Bạn hiện chỉ có thể thao tác trực
          tiếp trên bản đồ SLAM gốc.
        </div>
      )}

      <ControlRow label="Zoom sơ đồ">
        <ControlButton
          ariaLabel="Thu nhỏ sơ đồ"
          disabled={!canZoomOut}
          className={`h-7 w-7 text-base ${controlButtonClass}`}
          onClick={actions.onZoomOut}
          label="-"
        />
        <ControlButton
          ariaLabel="Phóng to sơ đồ"
          disabled={!canZoomIn}
          className={`h-7 w-7 text-base ${controlButtonClass}`}
          onClick={actions.onZoomIn}
          label="+"
        />
        <ResetControlButton
          ariaLabel="Khôi phục zoom sơ đồ"
          className="h-7 w-7"
          iconClassName="text-[14px]"
          onClick={actions.onResetZoom}
        />
      </ControlRow>

      {hasBackgroundImage && !requiresHomePlacement && (
        <div className="mt-2 rounded-xl border border-gray-100 bg-gradient-to-br from-gray-50 to-white px-2.5 py-2">
          <div className="mb-1 text-[11px] font-semibold text-gray-600">
            Chỉnh SLAM theo nền
          </div>
          <ControlRow
            label="Scale"
            className="border-none bg-transparent px-0 py-1 shadow-none"
            labelClassName="text-[10px] text-gray-500"
          >
            <ControlButton
              ariaLabel="Giảm scale SLAM"
              disabled={!canDecreaseOverlayScale}
              className={`h-6 w-6 text-sm ${controlButtonClass}`}
              onClick={actions.onDecreaseOverlayScale}
              label="-"
            />
            <ControlButton
              ariaLabel="Tăng scale SLAM"
              disabled={!canIncreaseOverlayScale}
              className={`h-6 w-6 text-sm ${controlButtonClass}`}
              onClick={actions.onIncreaseOverlayScale}
              label="+"
            />
            <ResetControlButton
              ariaLabel="Khôi phục scale SLAM"
              className="h-6 w-6"
              iconClassName="text-[12px]"
              onClick={actions.onResetOverlayScale}
            />
          </ControlRow>
          <ControlRow
            label="Xoay"
            className="mt-1 border-none bg-transparent px-0 py-1 shadow-none"
            labelClassName="text-[10px] text-gray-500"
          >
            <ControlButton
              ariaLabel="Xoay trái SLAM"
              className={`h-6 w-6 text-[12px] ${controlButtonClass}`}
              onClick={actions.onRotateOverlayLeft}
              label="<"
            />
            <ControlButton
              ariaLabel="Xoay phải SLAM"
              className={`h-6 w-6 text-[12px] ${controlButtonClass}`}
              onClick={actions.onRotateOverlayRight}
              label=">"
            />
            <ResetControlButton
              ariaLabel="Khôi phục xoay SLAM"
              className="h-6 w-6"
              iconClassName="text-[12px]"
              onClick={actions.onResetOverlayRotation}
            />
          </ControlRow>
        </div>
      )}
    </div>
  )
}

function HoverInfoBadge({
  x,
  y,
  scale,
  hasBackgroundImage,
  overlayScale,
  overlayRotationDeg,
}: HoverInfoBadgeProps) {
  return (
    <div className="absolute bottom-3 left-3 z-20 flex max-w-[calc(100%-16px)] items-center gap-2 rounded-xl border border-gray-100 bg-white/92 px-3 py-1.5 text-[12px] font-mono text-gray-600 shadow-sm backdrop-blur-md md:left-4 md:max-w-none">
      <Crosshair className="w-3.5 h-3.5 text-[#CE7A58]" />
      <span>
        X: <strong className="text-gray-900">{x.toFixed(2)}</strong>
      </span>
      <span className="text-gray-300">|</span>
      <span>
        Y: <strong className="text-gray-900">{y.toFixed(2)}</strong>
      </span>
      <span className="text-gray-300">|</span>
      <span>
        Zoom: <strong className="text-gray-900">{scale.toFixed(1)}x</strong>
      </span>
      {hasBackgroundImage && (
        <>
          <span className="text-gray-300">|</span>
          <span>
            Scale:{' '}
            <strong className="text-gray-900">{overlayScale.toFixed(2)}</strong>
          </span>
          <span className="text-gray-300">|</span>
          <span>
            Xoay:{' '}
            <strong className="text-gray-900">{overlayRotationDeg}°</strong>
          </span>
        </>
      )}
    </div>
  )
}

export function SlamFloorplanCanvas(props: SlamFloorplanCanvasProps) {
  const { isConfirmingAddNew = false, onConfirmAddNew } = props
  const [isControlPanelCollapsed, setIsControlPanelCollapsed] = useState(true)

  const slamTips = (
    <div className="max-w-[260px] text-[11px] leading-relaxed text-slate-100">
      <div className="mb-1 border-b border-slate-600 pb-1 font-medium">
        Có thể căn chỉnh lớp SLAM trực tiếp trên nền sơ đồ để hiển thị chính xác
        hơn.
      </div>
      <div>Cuộn chuột để phóng to hoặc thu nhỏ sơ đồ.</div>
      <div>Kéo thả để di chuyển sơ đồ khi đã phóng to.</div>
      <div>Shift + cuộn chuột để phóng to/thu nhỏ lớp SLAM.</div>
      <div>Shift + kéo để xoay lớp SLAM.</div>
    </div>
  )

  const {
    containerRef,
    canvasRef,
    scale,
    overlayScale,
    overlayRotationRad,
    canZoomIn,
    canZoomOut,
    canIncreaseOverlayScale,
    canDecreaseOverlayScale,
    hasBackgroundImage,
    contextMenu,
    hoveredCoords,
    setContextMenu,
    handlers,
    actions,
    requiresHomePlacement,
  } = useSlamCanvasInteraction(props)

  const overlayRotationDeg = ((overlayRotationRad * 180) / Math.PI).toFixed(1)

  return (
    <div
      ref={containerRef}
      role="img"
      aria-label="Sơ đồ sàn từ SLAM"
      className="relative h-full w-full select-none touch-none overflow-hidden"
      onClick={handlers.onClick}
      onContextMenu={handlers.onContextMenu}
      onPointerDown={handlers.onPointerDown}
      onPointerMove={handlers.onPointerMove}
      onPointerUp={handlers.onPointerUp}
      onPointerCancel={handlers.onPointerUp}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

      <SlamControlPanel
        isControlPanelCollapsed={isControlPanelCollapsed}
        onCollapseChange={setIsControlPanelCollapsed}
        requiresHomePlacement={requiresHomePlacement}
        hasBackgroundImage={hasBackgroundImage}
        canZoomIn={canZoomIn}
        canZoomOut={canZoomOut}
        canIncreaseOverlayScale={canIncreaseOverlayScale}
        canDecreaseOverlayScale={canDecreaseOverlayScale}
        actions={actions}
      />

      {hasBackgroundImage && !requiresHomePlacement && (
        <Tooltip
          placement="topRight"
          title={slamTips}
          overlayInnerStyle={{ backgroundColor: '#1f2937', color: '#f8fafc' }}
        >
          <Button
            htmlType="button"
            variant="outline"
            size="md"
            aria-label="Mẹo thao tác sơ đồ"
            className="absolute bottom-3 right-3 z-30 h-10 w-10 min-w-10 rounded-full border border-[#A85D3D] bg-white p-0 text-[14px] font-semibold text-[#A85D3D] shadow-sm hover:!bg-[#CE7A58] hover:!text-[#fef3f0]"
            onPointerDown={stopPointerDown}
            onClick={(e) => stopClick(e)}
          >
            ?
          </Button>
        </Tooltip>
      )}

      {hoveredCoords && !requiresHomePlacement && (
        <HoverInfoBadge
          x={hoveredCoords.x}
          y={hoveredCoords.y}
          scale={scale}
          hasBackgroundImage={hasBackgroundImage}
          overlayScale={overlayScale}
          overlayRotationDeg={overlayRotationDeg}
        />
      )}

      {contextMenu && (
        <div
          role="presentation"
          style={{ left: contextMenu.mouseX, top: contextMenu.mouseY }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          className="absolute z-50"
        >
          <SlamContextMenu
            x={0}
            y={0}
            markerId={contextMenu.markerId}
            hasName={contextMenu.hasName}
            isConfirmingAddNew={isConfirmingAddNew}
            onAdd={async () => {
              try {
                await onConfirmAddNew?.()
              } finally {
                setContextMenu(null)
              }
            }}
            onMoveRobot={
              props.onMoveRobot
                ? () => {
                    if (!props.onMoveRobot) return
                    actions.onMoveRobot(props.onMoveRobot)
                  }
                : undefined
            }
            onGoHome={
              props.onGoHome
                ? () => {
                    if (!props.onGoHome) return
                    actions.onGoHome(props.onGoHome)
                  }
                : undefined
            }
          />
        </div>
      )}
    </div>
  )
}
